// Type-only on purpose: a value import here adds a module edge from utils into
// xstate, which closes an import cycle and trips `tableStore` TDZ at startup.
import type {
  BotType,
  GetBotByIdData,
} from '../xstate/prostudio.api-service.types';

/** The subset of a `Solution` this needs, kept structural so it stays unit-testable. */
export type SolutionVersionForSelection = {
  id?: string | null;
  name?: string | null;
  version?: number | null;
  botSolutionVersionId?: string | null;
  isDraft?: boolean | null;
};

export type SolutionForSelection = {
  id?: string | null;
  name?: string | null;
  type?: unknown;
  settings?: unknown;
  defaultDraftVersionId?: string | null;
  solutionVersions?: (SolutionVersionForSelection | null)[] | null;
};

const isBotType = (value: unknown): value is BotType =>
  value === 'main' || value === 'survey';

/**
 * Main or survey. The source is the `Solution.type` column (backend #1417), which
 * `updateSolution(type)` writes. The column is NOT NULL with a `main` default, so
 * against that schema it always answers and the `settings.bot_type` fallback never
 * runs; the fallback only covers a response without the field. Settings must not
 * come first: the backend seeds `bot_type: 'main'` there at creation and the
 * switch never updates it, so it would undo every switch. Anything unrecognised
 * falls back to `main`.
 */
export const readBotType = (
  solution: { type?: unknown; settings?: unknown } | null | undefined
): BotType => {
  if (isBotType(solution?.type)) return solution.type;
  const fromSettings = (
    solution?.settings as { bot_type?: unknown } | null | undefined
  )?.bot_type;
  return isBotType(fromSettings) ? fromSettings : 'main';
};

/**
 * A legacy version carries `name: null` and keeps its name in the last segment of the
 * dotted `botSolutionVersionId` (`<customer>.<bot>.v1`). Reading it back matters: the
 * name is what the legacy `tenancy_solution_versions_ps` key is built from, so dropping
 * these versions left the solution with an empty version list, and `findDraftVersion`
 * then fabricated the literal string `"vundefined"` from it.
 */
export const readVersionName = (version: {
  name?: string | null;
  botSolutionVersionId?: string | null;
}): string | undefined => {
  if (version.name) {
    return version.name;
  }

  const legacyName = version.botSolutionVersionId?.split('.').pop();

  return legacyName || undefined;
};

const byOrdinal = (
  a: SolutionVersionForSelection,
  b: SolutionVersionForSelection
): number => (a.version ?? 0) - (b.version ?? 0);

/**
 * The draft the editor opens.
 *
 * Draft-only versioning (backend `docs/solution-versioning-redesign.md`): the draft is
 * permanent, and publishing snapshots it onto a separate `v#` slot instead of forking it
 * forward. So the draft is no longer "the newest version" — on a solution created under
 * the new model it is named `draft` and has `version: null`, which sorts FIRST — and
 * `latestVersionId` now names the last slot, not the draft.
 *
 * Resolution order:
 * 1. `Solution.defaultDraftVersionId` — the one draft that always exists;
 * 2. an `isDraft` version, newest first (a caller that did not select the pointer);
 * 3. the newest version by ordinal — a schema with no `isDraft` at all, where that
 *    really was the draft.
 */
export const pickDraftVersion = (
  solutionVersions: SolutionForSelection['solutionVersions'],
  defaultDraftVersionId?: string | null
): SolutionVersionForSelection | undefined => {
  const candidates = (solutionVersions ?? []).filter(
    (version): version is SolutionVersionForSelection =>
      version != null && Boolean(readVersionName(version))
  );

  if (defaultDraftVersionId) {
    const byPointer = candidates.find(
      (version) => version.id === defaultDraftVersionId
    );
    if (byPointer) {
      return byPointer;
    }
  }

  const drafts = candidates.filter((version) => version.isDraft);
  if (drafts.length) {
    return drafts.sort(byOrdinal)[drafts.length - 1];
  }

  // No version carries `isDraft: true`. Only then is the newest version the draft;
  // once the flag exists, a `false` on every row means "no draft known", and handing
  // out a published slot as the draft would let the editor write into it.
  if (candidates.some((version) => version.isDraft === false)) {
    return undefined;
  }

  return candidates.slice().sort(byOrdinal)[candidates.length - 1];
};

/**
 * The suffix shown after "Draft" in the UI. A draft created under draft-only versioning
 * is itself named `draft` (or `draft-2` …), so printing the name gave "Draft draft"; a
 * migrated solution's draft keeps its `vN` name, which is still worth showing.
 */
export const readDraftLabelSuffix = (
  draftName: string | null | undefined
): string => {
  if (!draftName) return '';
  if (draftName === 'draft') return '';
  const numbered = /^draft-(\d+)$/.exec(draftName);
  return numbered ? numbered[1] : draftName;
};

/**
 * Version names with the DRAFT LAST — the invariant every consumer of
 * `selectedBot.versions` relies on (the last entry is the draft, the rest are the
 * published history). Publish slots are ordered by their ordinal; drafts other than the
 * one the editor opens are left out, since nothing here can address them yet. Entries
 * with no derivable name are dropped — the Agent Manager APIs take the name as
 * `versionName`.
 */
export const readVersionNames = (
  solutionVersions: SolutionForSelection['solutionVersions'],
  defaultDraftVersionId?: string | null
): string[] => {
  const draft = pickDraftVersion(solutionVersions, defaultDraftVersionId);

  const slots = (solutionVersions ?? [])
    .filter(
      (version): version is SolutionVersionForSelection =>
        version != null && version !== draft && !version.isDraft
    )
    .sort(byOrdinal);

  return [...slots, ...(draft ? [draft] : [])]
    .map((version) => readVersionName(version))
    .filter((name): name is string => Boolean(name));
};

/**
 * Builds the `selectedBot` context entry from a Solution, replacing Bot Manager's
 * `getBotById`.
 *
 * Two identity notes, because the old shape conflated things:
 * - `botName` is the solution's machine `name`, which is what `${customerId}.${botName}`
 *   composite ids are built from elsewhere.
 * - `id` is intentionally the solution uuid, NOT the old `botSolutionId`. New solutions
 *   have no `botSolutionId` at all, so anything matching on it would match every new
 *   solution at once.
 *
 * The draft is `pickDraftVersion`'s, and `readVersionNames` puts it last.
 */
export const buildSelectedSolution = ({
  solution,
  displayName,
  assets = [],
}: {
  solution: SolutionForSelection;
  displayName: string;
  assets?: unknown[];
}): GetBotByIdData & { displayName: string; uniqueId: string } => {
  const versions = readVersionNames(
    solution.solutionVersions,
    solution.defaultDraftVersionId
  );

  return {
    id: solution.id ?? undefined,
    uniqueId: solution.id ?? '',
    displayName,
    botName: solution.name ?? undefined,
    botType: readBotType(solution),
    versions,
    draftVersion: versions[versions.length - 1],
    assets,
  };
};
